import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Boxes, LayoutDashboard, FileText, TriangleAlert, Ship, BarChart3, Settings,
  Sparkles, LogOut, Menu, X, FileSpreadsheet, ClipboardList, Mail, PackageMinus, Users,
} from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/app/products", label: "Inventory", icon: Boxes, id: "products" },
  { to: "/app/invoices", label: "Xero Invoices", icon: FileText, id: "invoices", modes: ["full_xero"] },
  { to: "/app/sales", label: "Sales & Issues", icon: PackageMinus, id: "sales", modes: ["xero_purchases", "spreadsheet"] },
  { to: "/app/gaps", label: "Gap Engine", icon: TriangleAlert, id: "gaps" },
  { to: "/app/digest", label: "Daily Digest", icon: Mail, id: "digest" },
  { to: "/app/stocktakes", label: "Stocktakes", icon: ClipboardList, id: "stocktakes" },
  { to: "/app/purchasing", label: "Purchasing", icon: Ship, id: "purchasing" },
  { to: "/app/uploads", label: "Excel Uploads", icon: FileSpreadsheet, id: "uploads" },
  { to: "/app/reports", label: "Reports", icon: BarChart3, id: "reports" },
  { to: "/app/assistant", label: "Setup Assistant", icon: Sparkles, id: "assistant" },
  { to: "/app/team", label: "Team", icon: Users, id: "team" },
  { to: "/app/settings", label: "Settings", icon: Settings, id: "settings" },
];

export const AppShell = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const mode = user?.org?.settings?.operating_mode || "full_xero";

  const isActive = (to) =>
    to === "/app" ? location.pathname === "/app" : location.pathname.startsWith(to);

  const nav = (
    <nav className="flex flex-col gap-0.5" data-testid="main-nav">
      {NAV.filter((item) => !item.modes || item.modes.includes(mode)).map((item) => (
        <Link
          key={item.to}
          to={item.to}
          data-testid={`nav-${item.id}`}
          onClick={() => setOpen(false)}
          className={`flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
            isActive(item.to)
              ? "bg-slate-900 text-white"
              : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
          }`}
        >
          <item.icon className="h-4 w-4" strokeWidth={1.75} />
          {item.label}
        </Link>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-slate-200 bg-white px-4">
        <div className="flex items-center gap-3">
          <button
            className="lg:hidden"
            data-testid="mobile-nav-toggle"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <Link to="/app" className="flex items-center gap-2" data-testid="brand-link">
            <span className="grid h-7 w-7 place-items-center bg-slate-900 text-[11px] font-bold text-white">S3</span>
            <span className="font-display text-base font-semibold">Stock360</span>
          </Link>
          <span className="hidden text-xs text-slate-500 sm:inline">
            {user?.org?.name}
          </span>
          <span className="hidden bg-slate-100 px-2 py-0.5 text-[10px] uppercase tracking-widest text-slate-600 md:inline" data-testid="operating-mode-badge">
            {mode === "full_xero" ? "Full Xero" : mode === "xero_purchases" ? "Xero purchases only" : "Spreadsheet only"}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden text-xs text-slate-500 md:inline" data-testid="current-user-email">
            {user?.email} · <span className="uppercase tracking-wider">{user?.role}</span>
          </span>
          <button
            data-testid="logout-button"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            className="flex items-center gap-1.5 border border-slate-200 px-2.5 py-1.5 text-xs text-slate-600 transition-colors hover:bg-slate-900 hover:text-white"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </header>

      <div className="flex">
        <aside className="hidden w-56 shrink-0 border-r border-slate-200 bg-white p-2 lg:block">
          <div className="sticky top-16">{nav}</div>
        </aside>
        {open && (
          <div className="fixed inset-x-0 top-14 z-20 border-b border-slate-200 bg-white p-2 lg:hidden">
            {nav}
          </div>
        )}
        <main className="min-w-0 flex-1 p-4 md:p-6">{children}</main>
      </div>
    </div>
  );
};

export const PageHeader = ({ title, subtitle, actions, testId }) => (
  <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between" data-testid={testId}>
    <div>
      <h1 className="font-display text-2xl font-semibold text-slate-900">{title}</h1>
      {subtitle && <p className="mt-1 max-w-2xl text-sm text-slate-500">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const Panel = ({ title, right, children, testId, className = "" }) => (
  <section className={`border border-slate-200 bg-white ${className}`} data-testid={testId}>
    {(title || right) && (
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5">
        <h2 className="font-display text-sm font-semibold text-slate-900">{title}</h2>
        {right}
      </div>
    )}
    <div>{children}</div>
  </section>
);

export const EmptyState = ({ icon: Icon = Boxes, title, body, action, testId }) => (
  <div className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center" data-testid={testId}>
    <Icon className="h-7 w-7 text-slate-300" strokeWidth={1.5} />
    <div>
      <p className="font-display text-sm font-semibold text-slate-800">{title}</p>
      <p className="mx-auto mt-1 max-w-sm text-sm text-slate-500">{body}</p>
    </div>
    {action}
  </div>
);

export const SkeletonRows = ({ rows = 6 }) => (
  <div className="divide-y divide-slate-100" data-testid="skeleton-rows">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex items-center gap-4 px-4 py-3">
        <div className="h-3 w-28 animate-pulse bg-slate-100" />
        <div className="h-3 flex-1 animate-pulse bg-slate-100" />
        <div className="h-3 w-16 animate-pulse bg-slate-100" />
      </div>
    ))}
  </div>
);

export const Btn = ({ variant = "primary", className = "", ...props }) => {
  const base =
    "inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-slate-400";
  const styles = {
    primary: "bg-slate-900 text-white hover:bg-slate-700",
    outline: "border border-slate-300 text-slate-700 hover:bg-slate-900 hover:text-white",
    danger: "border border-red-200 text-red-600 hover:bg-red-600 hover:text-white",
    ghost: "text-slate-600 hover:text-slate-900",
  };
  return <button className={`${base} ${styles[variant]} ${className}`} {...props} />;
};

export const Tag = ({ children, tone = "slate", testId }) => {
  const tones = {
    slate: "bg-slate-100 text-slate-700",
    green: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    red: "bg-red-50 text-red-700",
    violet: "bg-violet-50 text-violet-700",
    blue: "bg-blue-50 text-blue-700",
  };
  return (
    <span data-testid={testId} className={`inline-block px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider ${tones[tone]}`}>
      {children}
    </span>
  );
};
